import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UploadService } from '../../../../services/upload.service';
import { FrontendLogService } from '../../../../services/frontend-log.service';
import {
  DatabaseField,
  DataUploadRequest,
  PreviewData,
  ProcessedRecord,
  DetailedTableRecord,
  DetailedRecord,
  TableRecord,
  MultiValueEntry
} from '../../../../interfaces/upload.interface';

@Component({
  selector: 'app-data-preview-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatTableModule,
    MatChipsModule,
    MatExpansionModule,
    MatProgressBarModule
  ],
  templateUrl: './data-preview-dialog.component.html',
  styleUrl: './data-preview-dialog.component.scss'
})
export class DataPreviewDialogComponent implements OnInit, OnDestroy {
  allDatabaseFields: DatabaseField[] = []; // Az összes adatbázis mező a sémából
  isUploading = false; // Loading állapot
  
  // Progress bar állapot
  uploadProgress = 0;
  estimatedCurrentRecord = 0;
  totalRecordsToUpload = 0;
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    public dialogRef: MatDialogRef<DataPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PreviewData,
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef,
    private frontendLogService: FrontendLogService
  ) {}

  ngOnInit(): void {
    this.frontendLogService.addLog('📊 Preview data megjelenítve');
    this.frontendLogService.addLog(`   Mappings: ${this.data.mappings.length} db`);
    this.frontendLogService.addLog(`   Processed data: ${this.data.processedData.length} rekord`);
    this.loadAllDatabaseFields();
    
    // Total records count inicializálása
    this.totalRecordsToUpload = this.getTotalRowCount();
  }
  
  ngOnDestroy(): void {
    this.stopProgressSimulation();
  }
  
  /**
   * Progress szimuláció indítása
   * Becslés alapján növeli a progress-t, figyelembe véve a rekordok számát
   */
  private startProgressSimulation(): void {
    this.uploadProgress = 0;
    this.estimatedCurrentRecord = 0;
    
    // Rekordszám alapján becsült idő (ms/rekord) - körülbelül 50-100ms rekordonként
    const msPerRecord = 30;
    const totalEstimatedTime = this.totalRecordsToUpload * msPerRecord;
    
    // Progress frissítés intervalluma (100ms)
    const updateInterval = 100;
    
    // Minden frissítésnél ennyivel növekszik a progress (max 90%-ig)
    const maxProgress = 90;
    const progressIncrement = (maxProgress / (totalEstimatedTime / updateInterval));
    
    this.progressInterval = setInterval(() => {
      if (this.uploadProgress < maxProgress) {
        this.uploadProgress += progressIncrement;
        
        // Becsült aktuális rekord számítása
        this.estimatedCurrentRecord = Math.min(
          Math.floor((this.uploadProgress / 100) * this.totalRecordsToUpload),
          this.totalRecordsToUpload - 1
        );
        
        this.cdr.detectChanges();
      }
    }, updateInterval);
  }
  
  /**
   * Progress szimuláció leállítása
   */
  private stopProgressSimulation(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
  
  /**
   * Progress befejezése (100%-ra állítás)
   */
  private completeProgress(): void {
    this.stopProgressSimulation();
    this.uploadProgress = 100;
    this.estimatedCurrentRecord = this.totalRecordsToUpload;
    this.cdr.detectChanges();
  }

  onCancel(): void {
    this.stopProgressSimulation();
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    if (this.isUploading) return;
    
    // Async change detection fix
    setTimeout(() => {
      this.isUploading = true;
      this.startProgressSimulation();
      this.cdr.detectChanges();
      
      // Frontend logok összegyűjtése és hozzáadása a requesthez
      const frontendLogs = this.frontendLogService.getLogs();
      
      // KRITIKUS: fullProcessedData használata, ha létezik (minden sor), különben processedData (előnézet)
      const dataToUpload = this.data.fullProcessedData || this.data.processedData;
      this.frontendLogService.addLog(`🚀 Adatok feltöltése kezdődik - ${dataToUpload.length} rekord`);
      
      const uploadRequest: DataUploadRequest = {
        mappings: this.data.mappings,
        processedData: dataToUpload,
        frontendLogs: frontendLogs,
        enrichExisting: this.data.enrichExisting ?? false
      };
      
      this.uploadService.uploadProcessedData(uploadRequest).subscribe({
        next: (response) => {
          this.completeProgress();
          this.frontendLogService.addLog('✅ Feltöltés sikeres: ' + response.total_records + ' rekord');
          
          // Kis késleltetés, hogy a 100% látható legyen
          setTimeout(() => {
            this.isUploading = false;
            this.cdr.detectChanges();
            this.dialogRef.close({
              success: true,
              result: response
            });
          }, 500);
        },
        error: (error) => {
          console.error('Feltöltési hiba:', error);
          this.stopProgressSimulation();
          this.isUploading = false;
          this.cdr.detectChanges();
          
          // Error objektum normalizálása
          let errorMessage = 'Ismeretlen hiba';
          if (error?.error?.detail) {
            errorMessage = error.error.detail;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (typeof error === 'string') {
            errorMessage = error;
          } else if (error?.statusText) {
            errorMessage = `HTTP ${error.status}: ${error.statusText}`;
          }
          
          this.dialogRef.close({
            success: false,
            error: {
              message: errorMessage,
              details: error
            }
          });
        }
      });
    });
  }

  getTableCount(): number {
    const tables = new Set<string>();
    this.data.processedData.forEach(record => {
      Object.keys(record.tables).forEach(tableName => tables.add(tableName));
    });
    return tables.size;
  }

  getTotalRowCount(): number {
    // Ha van fullProcessedData, azt használjuk, különben a processedData length-et
    return this.data.fullProcessedData ? this.data.fullProcessedData.length : this.data.processedData.length;
  }

  getTableNames(): string[] {
    const tables = new Set<string>();
    this.data.processedData.forEach(record => {
      Object.keys(record.tables).forEach(tableName => tables.add(tableName));
    });
    return Array.from(tables).sort();
  }

  getTableDisplayName(tableName: string): string {
    const displayNames: { [key: string]: string } = {
      'companies': 'Cégek',
      'customers': 'Ügyfelek',
      'customeremails': 'Ügyfél Email-ek',
      'customerphones': 'Ügyfél Telefonok'
    };
    return displayNames[tableName] || tableName;
  }

  getFieldsForTable(tableName: string): string[] {
    const fields = new Set<string>();
    this.data.processedData.forEach(record => {
      if (record.tables[tableName]) {
        Object.keys(record.tables[tableName].fields).forEach(field => fields.add(field));
      }
    });
    return Array.from(fields).sort();
  }

  getFieldDisplayName(fieldName: string): string {
    // A mező név utolsó része az underscore után
    const parts = fieldName.split('_');
    if (parts.length > 1) {
      return parts.slice(1).join('_');
    }
    return fieldName;
  }

  getRecordCountForTable(tableName: string): number {
    return this.data.processedData.filter(record => record.tables[tableName]).length;
  }

  getSampleDataForTable(tableName: string): ProcessedRecord[] {
    return this.data.processedData
      .filter(record => record.tables[tableName])
      .slice(0, 3); // Csak 3 minta rekord
  }

  getMultiValueEntriesForTable(tableName: string): MultiValueEntry[] {
    const entries: MultiValueEntry[] = [];
    this.data.processedData.forEach(record => {
      record.multiValueEntries
        .filter(entry => entry.tableName === tableName)
        .forEach(entry => entries.push(entry));
    });
    return entries;
  }

  getTableCountForRecord(record: ProcessedRecord): number {
    return Object.keys(record.tables).length;
  }

  getTableNamesForRecord(record: ProcessedRecord): string[] {
    return Object.keys(record.tables).sort();
  }

  getFieldKeysForTable(record: ProcessedRecord, tableName: string): string[] {
    if (!record.tables[tableName]) return [];
    return Object.keys(record.tables[tableName].fields).sort();
  }

  getFieldValue(record: ProcessedRecord, tableName: string, field: string): string {
    return record.tables[tableName]?.fields[field] || '-';
  }

  getBaseRecordCount(): number {
    // Alap rekordok száma (Excel sorok száma tábla szerint)
    let count = 0;
    const tables = this.getTableNames();
    tables.forEach(tableName => {
      count += this.getRecordCountForTable(tableName);
    });
    return count;
  }

  getMultiValueRecordCount(): number {
    // Többértékű mezőkből származó extra rekordok
    let count = 0;
    this.data.processedData.forEach(record => {
      record.multiValueEntries.forEach(entry => {
        count += (entry.recordCount || entry.values.length) - 1; // -1 mert az első már benne van az alaprekordban
      });
    });
    return count;
  }

  getTotalRecordCount(): number {
    return this.getBaseRecordCount() + this.getMultiValueRecordCount();
  }

  loadAllDatabaseFields(): void {
    // Az összes adatbázis mező betöltése az API-ból
    this.uploadService.getDatabaseFields().subscribe({
      next: (response) => {
        this.allDatabaseFields = response.fields;
        this.cdr.detectChanges(); // Explicit change detection a template frissítéséhez
      },
      error: (error) => {
        console.error('Hiba az adatbázis mezők betöltése során:', error);
        // Fallback mock séma használata
        this.allDatabaseFields = [];
        this.cdr.detectChanges(); // Explicit change detection az üres tömb esetén is
      }
    });
  }

  getSchemaFieldsForTable(tableName: string): DatabaseField[] {
    return this.allDatabaseFields.filter(field => field.table === tableName);
  }

  isFieldMapped(fieldValue: string): boolean {
    return this.data.mappings.some(mapping => mapping.databaseColumn === fieldValue);
  }

  getFieldKeys(fields: { [key: string]: any }): string[] {
    return Object.keys(fields);
  }

  isLastRecord(records: any[], index: number): boolean {
    return index === records.length - 1;
  }

  isLastExcelRow(index: number): boolean {
    return index === this.data.processedData.length - 1;
  }

  isReferenceField(value: any): boolean {
    return typeof value === 'string' && value.includes('(hivatkozás');
  }
}
